import "./AssessmentDetailsView.scss";
import React, { useEffect, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import {
  ClinicalFormInfo,
  MemberAnswerInfo,
  MemberFormInfo,
  Problem,
} from "clinical-forms-experience-client";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import {
  formatDate,
  formatDateLong,
  formatTime,
} from "../../helper/dateFormatter";
import ClinicalFormDetailsView from "../ClinicalForms/ClinicalFormDetailsView";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import { createPgiApi } from "../../api-helpers/PgiApiHelper";
import { useNavigate } from "react-router-dom";
import { toTitleCase } from "../../helper/TextUtils";
import EditAssessmentView from "./EditAssessmentView";
import { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { communicationApi } from "../../api-helpers/CommunicationApiHelper";
import JobAidModal from "../Appointment/JobAidModal";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import { getScoreTrend } from "../Assessment-v2/utils.assessment";
import { PatientGoals, PatientIntervention, PatientPgiMapResponse } from "grid-experience-client";

interface PgiIds {
  problemId: string | undefined;
  goals: {
    goalId: string | undefined;
    interventions: (string | undefined)[];
  }[];
}

interface Props {
  patientId: string;
  memberFormId?: string;
  onClosed?: () => void;
}

const AssessmentDetailsView: React.FC<Props> = ({
  patientId,
  memberFormId,
  onClosed,
}: Props) => {
  const clinicalformsApi = createClinicalFormsApi();
  const pgiApi = createPgiApi();
  const gridApi = createGridApi();

  const [memberForm, setMemberForm] = useState<MemberFormInfo | null>(null);
  const [otherMemberForms, setOtherMemberForms] = useState<
    MemberFormInfo[] | null
  >(null);
  const [pgis, setPgis] = useState<PatientPgiMapResponse[]>([]);

  const [answers, setAnswers] = useState<MemberAnswerInfo[]>();

  const [scoreMovement, setScoreMovement] = useState<number | null>(0);
  const [scoreTrend, setScoreTrend] = useState<string | null>(null);
  const [pamLevel, setPamLevel] = useState<number | null>(0);
  const [gridReloadKey, setGridReloadKey] = useState(0);
  const [showAssessmentPGIs, setShowAssessmentPGIs] = useState(false);
  const [clinicalforms, setClinicalForms] = useState<ClinicalFormInfo[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [pgiIds, setPgiIds] = useState<PgiIds[]>([]);
  const [showJobAidModal, setShowJobAidModal] = useState(false);
  const [jobAidUrl, setJobAidUrl] = useState<string>('');
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });
  const [showViewAllButton, setShowViewAllButton] = useState(false);


  const commApi = communicationApi();

  const [interventionImages, setInterventionImages] = useState<Map<string, string>>(new Map());

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  const handleViewAllClick = () => {
    setShowAssessmentPGIs(true);
  };

  const handleShowJobAidModal = async (imageLocation: string) => {
    if (imageLocation === "") {
      setShowJobAidModal(false);
      return;
    }
    const communicationResponse = await commApi.communicationControllerGetSignedUrl(imageLocation);
    setJobAidUrl(communicationResponse.data);

    setShowJobAidModal(true);
  };


  const navigate = useNavigate();

  const handleAddPgiForm = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig);
    setShowToast(true);
  };

  const pamTitle = "Patient Activation Measure (PAM)"

  useEffect(() => {
    if (!memberFormId) {
      return;
    }

    const loadClinicalForms = async () => {
      LoadingHelper.showLoader(".assessment-details-view");
      try {
        // Load member form
        const memberFormsResponse = (
          await clinicalformsApi.clinicalFormsControllerGetMemberForms(
            patientId,
            ClinicalFormsConstants.assessmentType
          )
        ).data;

        const currentMemberForm = memberFormsResponse.find(
          (m: any) => m.id === memberFormId
        );
        if (!currentMemberForm) {
          console.error("Member form not found");
          return;
        }
        updateScoreMovement();
        // Set current member form and other member forms
        setMemberForm(currentMemberForm);
        setOtherMemberForms(
          memberFormsResponse.filter(
            (m: MemberFormInfo) => m.formId === currentMemberForm.formId && m.status !== ClinicalFormsConstants.draftStatus
          )
        );

        const response = await clinicalformsApi.clinicalFormsControllerGetClinicalForms(ClinicalFormsConstants.assessmentType);
        const clinicalFormsData = response.data;
        setClinicalForms(clinicalFormsData);
        const { data } = await clinicalformsApi.clinicalFormsControllerGetPgisForMemberForm(memberFormId!);
        const newInterventionImages = new Map<string, string>();
        if (data.length > 0) {
          data.forEach((pgi) => {
            pgi.goals.forEach((goal) => {
              goal.interventions.forEach((intervention) => {
                if (intervention.imageLocation && intervention.id) {
                  newInterventionImages.set(intervention.id, intervention.imageLocation);
                }
              });
            });
          });
          setInterventionImages(newInterventionImages);
        }

      } finally {
        LoadingHelper.hideLoader(".assessment-details-view");
      }
    };
    loadClinicalForms();
  }, [patientId, memberFormId]);

  useEffect(() => {
    if (!memberForm) {
      return;
    }

    const getPgi = async () => {
      LoadingHelper.showLoader(".pgis-view");
      try {
        const pgiResponse = await gridApi.gridControllerGetPatientPgiMap(
          patientId!,
          memberForm.id
        );
        const pgiResult = pgiResponse.data;
        if (pgiResult) {
          const pgiIds = pgiResult.map(problem => ({
            problemId: problem.problem?.problemId,
            goals: problem.goal.map(goal => ({
              goalId: goal.goal?.goalId,
              interventions: goal.interventions.map(intervention => intervention.intervention?.interventionId)
            }))
          }));
          await setPgis(pgiResult);
          setPgiIds(pgiIds);
        }
      } catch (error) {
        console.log(error);
      } finally {
        LoadingHelper.hideLoader(".pgis-view");
      }
    };

    const loadAnswers = async () => {
      LoadingHelper.showLoader(".clinical-form-details");
      try {
        const answers = (
          await clinicalformsApi.clinicalFormsControllerGetMemberAnswers(
            memberForm.id
          )
        ).data;
        setAnswers(answers.formAnswers);
      } finally {
        LoadingHelper.hideLoader(".clinical-form-details");
      }
    };
    getPgi();

    loadAnswers();

    getPamLevel();

    updateScoreMovement();
  }, [memberFormId, memberForm, showAssessmentPGIs]);

  const updateScoreMovement = () => {
    if (!memberForm || !otherMemberForms) {
      return;
    }

    setScoreMovement(null);
    setScoreTrend(null);
    const currentScore = memberForm.score;
    if (!currentScore) {
      return
    }
    otherMemberForms.forEach((form, index) => {
      if (form.id === memberForm.id) {
        if (index === otherMemberForms.length - 1) {
          return;
        }

        let lastIndex = index + 1;
        while (lastIndex < otherMemberForms.length) {
          const previousForm = otherMemberForms[lastIndex];
          console.log(lastIndex, currentScore, previousForm.score);
          if (previousForm.score) {
            const delta = currentScore - previousForm.score;
            setScoreMovement(delta);
            const trend = getScoreTrend(memberForm.title || "", currentScore, previousForm.score);
            setScoreTrend(trend);
            return;
          }
          lastIndex++;
        }
      }
    });
  }

  const handleClick = () => {
    if (memberForm?.source === 'appointment') {
      navigate(`/schedule/charting/${memberForm.sourceRefId}`);
    }
  };

  const getPamLevel = async () => {
    const pamLevel = await clinicalformsApi.clinicalFormsControllerGetLevel(memberForm?.id!);
    setPamLevel(pamLevel.data);
  }

  useEffect(() => {
    if (!memberForm) {
      return;
    }

    const assessmentFormID = otherMemberForms && otherMemberForms.length > 0 ? otherMemberForms[0]?.id : '';

    setShowViewAllButton(assessmentFormID == memberForm.id);

  }, [memberForm]);

  return (
    <>
      <JobAidModal
        jobAidUrl={jobAidUrl}
        showModal={showJobAidModal}
        handleModal={() => setShowJobAidModal(!showJobAidModal)}
      />
      {/* Render AssessmentPGIs when showAssessmentPGIs is true */}
      {showAssessmentPGIs ? (
        <div className="assessment-pgis-container">
          <EditAssessmentView
            onClosed={() => setShowAssessmentPGIs(false)}
            patientId={patientId!}
            memberFormId={memberFormId}
            handleAddPgiForm={handleAddPgiForm}
            reloadGrid={reloadGrid}
            clinicalForms={clinicalforms}
            pgiIds={pgiIds}
          />
        </div>
      ) : (
        <div className="assessment-details-view assessment">
          <div className="assessment-details-view assessment">
            <div className="assessment__aside ">
              <div className="border rounded">
                <div className="table-responsive">
                  <table className="table fs-12 m-0">
                    <thead>
                      <tr>
                        <th className="bg-teal-lighter" scope="col">
                          Sr.No
                        </th>
                        <th className="bg-teal-lighter" scope="col">
                          Assessment
                        </th>
                        <th className="bg-teal-lighter" scope="col">
                          Date
                        </th>
                        <th className="bg-teal-lighter" scope="col">
                          Score
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {otherMemberForms?.map((form, index) => (
                        <tr
                          key={form.id}
                          className={
                            form === memberForm
                              ? "member-forms-row member-forms-selected-row"
                              : "member-forms-row"
                          }
                          onClick={() => setMemberForm(form)}
                        >
                          <th scope="row">{index + 1}</th>
                          <td>{form.title}</td>
                          <td>{formatDate(form.createdOn)}</td>
                          <td>{form.score ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="assessment__main">
              <div className="px-4 py-3 border rounded-3">
                <div className="fs-12 text-light text-uppercase">Assessment</div>
                <strong className="fs-16">{memberForm?.title}</strong>
                <hr className="mt-2" />
                <div className="d-flex mt-2">
                  <div className="flex-fill">
                    <ul className="ap-list">
                      <li className="mt-0 mb-2">
                        <strong>Coach: {memberForm?.updatedBy}</strong>
                      </li>
                      <li className="my-2">
                        <span className="circle-icon bg-teal-light border-transparent me-3">
                          <span className="material-symbols-outlined menu__icon">
                            calendar_today
                          </span>
                        </span>{" "}
                        {formatDateLong(memberForm?.createdOn)}
                      </li>
                      <li className="my-2">
                        <span className="circle-icon bg-teal-light border-transparent me-3">
                          <span className="material-symbols-outlined menu__icon">
                            schedule
                          </span>
                        </span>
                        {formatTime(memberForm?.createdOn)}
                      </li>
                      <li className="my-2">
                        <span className="me-3">Source:</span>
                        <span className="d-block">
                          <button
                            className="btn btn-outline-primary btn-sm rounded-pill px-5"
                            type="button"
                            onClick={handleClick}
                          >
                            {toTitleCase(memberForm?.source)}
                          </button>
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <div className="d-flex align-items-center py-2 px-4 border rounded-3">
                      <div>
                        <strong className="fs-14">Score</strong>
                      </div>
                      <div className="px-2">
                        <span className="circle-icon circle-icon-md bg-teal-light fs-22 border-0">
                          {memberForm?.score ?? "-"}
                        </span>
                      </div>
                      {(scoreMovement ?? 0) !== 0 && scoreTrend && (<div>
                        <div className="d-flex align-items-top fs-11">
                          <span className={"material-symbols-outlined align-text-top " + (scoreTrend === "improving" ? "text-success" : "text-warning")}>
                            {(scoreTrend === "improving" ? "arrow_upward" : "arrow_downward")}
                          </span>
                          <span className={scoreTrend === "improving" ? "text-success" : "text-warning"}>
                            {(scoreMovement ?? 0) !== 0 && <span>{scoreMovement! > 0 ? "+" : "-"}</span>} {Math.abs(scoreMovement!).toFixed(2)}
                          </span>
                        </div>
                      </div>)}
                    </div>
                    {memberForm?.title === pamTitle ?
                      <span className="d-block badge bg-low fs-13">
                        PAM Level: <strong>{pamLevel}</strong>
                      </span> : null
                    }

                  </div>
                </div>
              </div>
              <div className="assessment-middle">
                {memberForm && (
                  <ClinicalFormDetailsView
                    formId={memberForm.formId}
                    comments={memberForm.comments ?? ""}
                    readOnly={true}
                    answers={answers}
                    hideComments={true}
                  />
                )}
              </div>
            </div>
            <div className="assessment__aside assessment__aside-lg pgis-view">

              {memberForm?.status !== ClinicalFormsConstants.discardedStatus &&
                <div className="border rounded">
                  <div className="pgi-table fs-12">
                    <>
                      <div className="row">
                        <div className="col-12 d-flex justify-content-between align-items-center">
                          <strong className="d-block py-2 px-3 fs-16 border-bottom">
                            Selected PGIs
                          </strong>
                          {showViewAllButton && <button
                            className="btn btn-sm btn-outline-secondary px-3 rounded-pill me-2"
                            onClick={handleViewAllClick}
                          >
                            View All
                          </button>}
                        </div>
                      </div>
                      <div className="d-flex bg-gray-FAF rounded rounded-top fw-bold">
                        <div className="flex-fill py-2 px-3">
                          <strong>PROBLEM - GOAL - INTERVENTION</strong>
                        </div>
                      </div>
                      {pgis.length === 0 ? (
                        <div className="text-center py-3">
                          <strong>No PGIs were selected</strong>
                        </div>
                      ) : (
                        <Accordion defaultActiveKey={[]} alwaysOpen>
                          {pgis.map((pgi: PatientPgiMapResponse, index: number) => {
                            return (
                              <Accordion.Item key={index} eventKey={`problem-${index}`}>
                                <div className="d-flex align-items-stretch border-top fs-12 problem-heading">
                                  <div className="w-flex-lg flex-fill py-2 px-4 border-start">
                                    <Accordion.Header>
                                      <a
                                        data-bs-toggle="collapse"
                                        href="#problem-001"
                                        className="accordion-link"
                                      >
                                        <strong className="d-block text-dark fw-bold">
                                          Problem {index + 1}: {pgi.problem?.problem}
                                        </strong>
                                        <span className="d-block fs-11 text-light">
                                          {pgi.problem?.problemDesc}
                                        </span>
                                      </a>
                                    </Accordion.Header>
                                  </div>
                                </div>
                                <Accordion.Body className="p-0">
                                  {pgi.goal &&
                                    pgi.goal.map((goal: PatientGoals, goalIndex: number) => (
                                      <Accordion.Item
                                        key={goalIndex}
                                        eventKey={`problem-${index}__goal-${goalIndex}`}
                                      >
                                        <div className="d-flex align-items-stretch border-top fs-12">
                                          <div className="w-flex-lg d-flex flex-fill align-items-center py-2 px-3 ps-12 border-start">
                                            <div className="flex-fill ps-1">
                                              <div>
                                                <Accordion.Header>
                                                  <a
                                                    href="#"
                                                    className="accordion-link"
                                                  >
                                                    <strong className="d-inline-block me-2">
                                                      <span className="text-light-black fw-600">
                                                        Goal {goalIndex + 1}: {goal.goal?.goal}
                                                      </span>
                                                    </strong>
                                                  </a>
                                                </Accordion.Header>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <Accordion.Body className="p-0">
                                          {goal &&
                                            goal.interventions.map(
                                              (
                                                intervention: PatientIntervention,
                                                interventionIndex: number
                                              ) => (
                                                <div
                                                  key={interventionIndex}
                                                  className="d-flex align-items-stretch border-top fs-12"
                                                >
                                                  <div className="w-flex-lg d-flex flex-fill align-items-center py-2 px-3 border-start ps-12">
                                                    <div className="flex-fill ps-6">
                                                      <div>
                                                        <span
                                                          className={`material-symbols-outlined me-1 align-text-top fs-20 text-secondary cursor-pointer ${interventionImages.has(intervention.intervention!.interventionId!) ? 'visible' : 'invisible'}`}
                                                          onClick={() => handleShowJobAidModal(interventionImages.get(intervention!.intervention!.interventionId!)!)}
                                                        >
                                                          quick_reference
                                                        </span>

                                                        <span className="intervention-badge badge badge-offwhite me-1 border">
                                                          Intervention
                                                        </span>
                                                        <span className="me-2 text-light-black">
                                                          {intervention.intervention?.intervention}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              )
                                            )}
                                        </Accordion.Body>
                                      </Accordion.Item>
                                    ))}
                                </Accordion.Body>
                              </Accordion.Item>
                            );
                          })}
                        </Accordion>
                      )}
                    </>
                  </div>
                </div>
              }
            </div>

            <div className="assessment__footer">
              <div className="text-light fs-12">
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill ms-2 px-5"
                  type="button"
                  onClick={onClosed}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default AssessmentDetailsView;
